import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Plus,
  Copy,
  CheckCircle2,
  Pencil,
  Trash2,
  KeyRound,
  UserMinus,
  UserPlus,
  Users,
  LayoutGrid,
  List as ListIcon,
  Send,
  Upload,
} from 'lucide-react'
import { Avatar } from '../../components/Avatar'
import {
  DUE_SOON_DAYS,
  buildTaskMonitorLink,
} from '../../lib/team-stats'
import { ExitHandoverDialog } from '../../components/ExitHandoverDialog'
import { MemberImportDialog } from '../../components/MemberImportDialog'
import { Pagination } from '../../components/ui/Pagination'
import { toast } from 'sonner'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { FilterRow } from '../../components/filters/FilterRow'
import {
  PhoneInputField,
  validatePhone,
} from '../../components/ui/PhoneInputField'
import { emailSchema, nameSchema } from '../../lib/validators'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Breadcrumb } from '../../components/Breadcrumb'
import { RolePill } from '../../components/ui/Pills'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { useSortable } from '../../lib/use-sortable'
import { api, extractApiError, isPlanLimitError } from '../../lib/api'
import { usePageSize } from '../../lib/display-prefs'
import { lengthError } from '../../lib/fieldLength'
import {
  departmentAssignmentError,
  departmentsToSave,
} from '../../lib/member-assignments'
import { formatDateTime } from '../../lib/format'
import { copyText } from '../../lib/clipboard'
import { useAuthStore } from '../../stores/authStore'
import {
  departmentList,
  inviteUserResponse,
  paginatedMembers,
  workRoleList,
  workspaceListResponse,
} from '../../lib/types'

// SubAdminAccessSummary + SUB_ADMIN_OP_ICONS removed 2026-05-22 — the
// read-only access summary card was dropped from both modals per user
// feedback. The SUB_ADMIN_OPERATIONS catalogue itself stays as the
// source of truth used by `requireAdminOp` / `canAdmin` /
// `hasSubAdminOperation` server- + client-side; this file just no
// longer renders it visually inside the Edit/Invite dialogs.

const inviteSchema = z.object({
  // Shared name + email validators — see lib/validators.js for the
  // canonical rule set (matches Sign-up / Profile / Tenant Create).
  fullName: nameSchema('Full Name'),
  emailId: emailSchema(),
  contactNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .superRefine((v, ctx) => {
      const r = validatePhone(v)
      if (!r.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: r.message ?? 'Please enter a valid Contact Number.',
        })
      }
    }),
  // The Company Admin can only ever invite a `user` or a `sub_admin` —
  // there's exactly one tenant owner per organisation, and that's the
  // CA who's already signed in. Promoting someone TO Company Admin is
  // a separate ownership-transfer flow that doesn't live in this form.
  role: z.enum(['user', 'sub_admin'], {
    message: 'Please select a Role.',
  }),
  /** Work Role only applies to `role: 'user'` accounts — Sub Admins
   *  sit above the Work Role hierarchy (they're delegated admins, not
   *  team members in a department), so the field is hidden when
   *  role === 'sub_admin'. The value is the catalogue row UUID (from
   *  `/work-roles`); validation that the row belongs to this tenant
   *  happens server-side. */
  workRoleId: z
    .union([z.literal(''), z.string().uuid()])
    .optional(),
  /** Workspace + Department live on the workspace_members row, not the
   *  user. The dialog supports adding the new user to many Workspaces
   *  (each with its own optional Department) in one submit; that list
   *  is held in local React state (`inviteAssignments`) and manually
   *  validated for min-1 in the submit handler — and skipped entirely
   *  when role === 'sub_admin' (delegated admins are tenant-level and
   *  never become workspace_members rows). */
  status: z.enum(['active', 'suspended']),
}).superRefine((data, ctx) => {
  // Work Role is required for `role === 'user'`. Empty role is
  // handled by the Role field's own required error, and Sub Admins
  // don't get a workRole. The Work Role field is hidden unless
  // `role === 'user'`, so showing an error for a hidden field would
  // confuse the user — gate the validation to match the visibility rule.
  if (data.role === 'user' && (!data.workRoleId || data.workRoleId === '')) {
    ctx.addIssue({
      path: ['workRoleId'],
      code: z.ZodIssueCode.custom,
      message: 'Please select a Work Role.',
    })
  }
  // Per-op cross-field validation removed 2026-05-22 — Sub Admin is
  // now an all-or-nothing role; the server auto-populates the grant
  // list when the role is assigned.
})

const editSchema = z.object({
  /** Sign-in Email ID. Same shared validator as the invite dialog, so the
   *  normalisation (trim + lowercase) matches the backend DTO exactly. */
  emailId: emailSchema(),
  fullName: nameSchema('Full Name'),
  contactNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .superRefine((v, ctx) => {
      const r = validatePhone(v)
      if (!r.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: r.message ?? 'Please enter a valid Contact Number.',
        })
      }
    }),
  // Same constraint as invite — neither dialog can mint a Company
  // Admin. Existing CA rows are read-only as far as Role is concerned
  // (the dropdown is hidden / disabled when editing the CA themselves).
  role: z.enum(['user', 'sub_admin'], {
    message: 'Please select a Role.',
  }),
  /** Catalogue row UUID — the dropdown is populated from `/work-roles`.
   *  An empty string is treated as "no Work Role yet" (legitimate for
   *  Sub Admins, who don't carry one). */
  workRoleId: z
    .union([z.literal(''), z.string().uuid()])
    .optional(),
  status: z.enum(['active', 'suspended']),
  /** Optional reason captured when suspending — shown to the member in
   *  their lockout message (with the admin contact) so they know why. */
  statusReason: z.string().max(500).optional(),
})

function roleLabel(r) {
  if (r === 'super_admin') return 'Master Admin'
  if (r === 'company_admin') return 'Company Admin'
  if (r === 'sub_admin') return 'Sub Admin'
  return 'User'
}

/** Role → RolePill label + tone. Mirrors the All Users (Master Admin)
 *  table so the Role column reads identically across both panels: the
 *  three admin tiers are amber, a plain User is violet. */
function roleBadge(r) {
  return { label: roleLabel(r), tone: r === 'user' ? 'violet' : 'amber' }
}

function workRoleLabel(w) {
  if (w === 'manager') return 'Manager'
  if (w === 'team_leader') return 'Team Leader'
  return 'Employee'
}

function statusLabel(s) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Company-wide visibility operations. A Work Role that grants any of these
// sees the whole company and so transcends Departments — picking such a role
// disables the Department field. Department / Self roles (which lack these)
// must belong to a Department. Mirrors the scope ops in WorkRolesPage's
// access-level model.
const COMPANY_SCOPE_OPS = [
  'tasks.view_workspace',
  'reports.view_workspace',
  'members.view_workspace',
  'leaderboard.view_workspace',
]
function isCompanyWideRole(role) {
  const ops = role?.operations ?? []
  return COMPANY_SCOPE_OPS.some((k) => ops.includes(k))
}

export function MembersPage() {
  const qc = useQueryClient()
  const [importOpen, setImportOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  // View — 'list' (the editable CRUD table) or 'grid' (read-only
  // task-signals cards, formerly the standalone Team page). The two
  // views were merged on 2026-06-08; `?view=grid` deep-links keep
  // working, the legacy `/team` route redirects here. List is the
  // default so a Company Admin landing fresh sees the management
  // surface they're most likely to want.
  const view = searchParams.get('view') === 'grid' ? 'grid' : 'list'
  function setView(nextView) {
    const next = new URLSearchParams(searchParams)
    if (nextView === 'grid') {
      next.set('view', 'grid')
    } else {
      next.delete('view')
    }
    setSearchParams(next, { replace: true })
  }
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  // Grid view shows ~100 cards comfortably (3 cols × ~33 rows of
  // scroll), so it starts at 100. List view starts at (and follows) the
  // person's account rows-per-page (My Preferences); GET /users accepts
  // every standard size up to 500, so no cap is needed. Each view keeps
  // its own size, so toggling views doesn't lose a size picked in the other.
  const [listPageSize, setListPageSize] = usePageSize()
  const [gridPageSize, setGridPageSize] = useState(100)
  const pageSize = view === 'grid' ? gridPageSize : listPageSize
  const setPageSize = view === 'grid' ? setGridPageSize : setListPageSize
  const [inviteOpen, setInviteOpen] = useState(false)
  // Declared up here (next to the other dialog-open flag) so the
  // per-workspace department queries further down can enable themselves
  // while EITHER the Add or the Edit dialog is open — the Edit dialog
  // reuses the same `inviteDeptsByWorkspace` map.
  const [editing, setEditing] = useState(null)
  const [issuedPassword, setIssuedPassword] = useState(null)

  // Department membership is per-workspace now. The page exposes an
  // optional Workspace filter at the top — when set, the table includes a
  // Department column with an inline editor and the row count is scoped
  // No Workspace filter on this page any more — the Department filter reads
  // from the company's primary Workspace (`effectiveWorkspaceId`). This stays
  // a constant '' so the member-list / per-member-department code below keeps
  // its "no specific Workspace picked" branch.
  const filterWorkspaceId = ''
  const [filterDepartmentId, setFilterDepartmentId] = useState('')
  // Work Role filter — initialised from the URL so other pages (e.g. the
  // Work Roles page's member counts) can deep-link straight here
  // (?workRoleId=…). Sourced from the tenant's live Work Role catalogue
  // below, so any role you create is filterable with no hardcoded tiers.
  const [filterWorkRoleId, setFilterWorkRoleId] = useState(
    () => searchParams.get('workRoleId') ?? '',
  )

  // A filter is "active" when the search box is non-empty or either the
  // Department or Work Role filter is set. Drives the Clear-all-filters
  // button, which only appears once at least one filter is narrowing
  // the list.
  const hasAnyFilter =
    search.trim() !== '' ||
    filterDepartmentId !== '' ||
    filterWorkRoleId !== ''

  function clearFilters() {
    setSearch('')
    setFilterDepartmentId('')
    setFilterWorkRoleId('')
    setPage(1)
  }

  // Debounce the search box — `useMemo` doesn't honour the returned
  // cleanup, so the previous timer wasn't cancelled on every keystroke
  // and the search effectively didn't debounce. `useEffect` is the
  // right primitive for a side effect with cleanup.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return workspaceListResponse.parse(res.data)
    },
  })
  const workspaces = workspacesQuery.data?.data ?? []
  // Workspace de-emphasis: the Members page no longer exposes a Workspace
  // filter (Departments are the only grouping the admin picks). Departments
  // are loaded from the company's primary Workspace — its only one for a
  // single-Workspace company. `effectiveWorkspaceId` falls back to that
  // primary Workspace since `filterWorkspaceId` is no longer set from the UI.
  const hasMultipleWorkspaces = workspaces.length > 1
  const primaryWorkspaceId = workspaces[0]?.id ?? ''
  const effectiveWorkspaceId = filterWorkspaceId || primaryWorkspaceId
  // The tenant's only Workspace (when there's exactly one). With a single
  // Workspace the Add/Edit Member dialogs drop the Workspace picker entirely
  // and just ask for a Department — the member is silently attached to this
  // Workspace. Empty string for multi-Workspace tenants, where the admin
  // still picks Workspaces explicitly.
  const soleWorkspaceId = hasMultipleWorkspaces ? '' : primaryWorkspaceId
  // The member LIST stays tenant-wide (so the Company Admin — who has no
  // Workspace membership — still appears) until a Department is actually
  // picked; only then does it scope to the Workspace so the backend can
  // join on that Department.
  const listWorkspaceId = filterDepartmentId ? effectiveWorkspaceId : ''

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'members',
      {
        search: debounced,
        page,
        pageSize,
        listWorkspaceId,
        filterDepartmentId,
        filterWorkRoleId,
      },
    ],
    queryFn: async () => {
      const res = await api.get('/users', {
        params: {
          search: debounced || undefined,
          page,
          pageSize,
          workspaceId: listWorkspaceId || undefined,
          departmentId:
            listWorkspaceId && filterDepartmentId
              ? filterDepartmentId
              : undefined,
          workRoleId: filterWorkRoleId || undefined,
        },
      })
      return paginatedMembers.parse(res.data)
    },
  })

  // Tenant-wide tasks — fetched once and shared across BOTH views so
  // a Company Admin sees the same Open · Due Soon · Overdue · Done
  // this week numbers regardless of which view they're in. Used to
  // be gated on `view === 'grid'` for cost, but a Manager looking at
  // the list version of the same roster was getting silently
  // different (i.e. empty) task-data treatment vs the grid — a real
  // parity bug. Shares Task Monitor's exact query key so this
  // surface benefits from any cache hit Task Monitor already
  // produced (and vice versa). When the user doesn't hold
  // `task_monitor.view`, the request 403s — we catch that silently
  // and the stats column / strip just shows "—".
  // Per-member Open · Due Soon · Overdue · Done — now computed SERVER-SIDE by
  // the SAME canonical rollup + feed the Team Overview page uses (GET
  // /me/member-task-stats), so a member's counts are identical on both pages.
  // (Previously the browser recomputed them from /admin/tasks via
  // buildStatsByUser — a second implementation that drifted from Team
  // Overview's.) Overdue / Due Soon are time-dependent, so poll once a minute:
  // the server re-evaluates against the current clock and picks up due-date
  // edits made elsewhere. 403 (no permission) falls back to an empty map so
  // the stat cells render "—".
  const tasksQuery = useQuery({
    queryKey: ['member-task-stats'],
    queryFn: async () => {
      try {
        const res = await api.get('/me/member-task-stats')
        return res.data?.stats ?? {}
      } catch (err) {
        if (err?.response?.status === 403) return {}
        throw err
      }
    },
    refetchInterval: 60_000,
  })
  const statsByUser = useMemo(
    () => new Map(Object.entries(tasksQuery.data ?? {})),
    [tasksQuery.data],
  )

  const rawRows = data?.data ?? []
  const pagination = data?.pagination
  // Department is ALWAYS rendered now (a fixed column in the DataTable
  // config below); when no Workspace is filtered the cell reads "—" (a
  // user's department is workspace-scoped, so without that context
  // there's nothing specific to show).
  // Workspaces column hides when a single Workspace is selected — every row
  // would read "1" which is pure noise. It ALSO hides for a company running
  // a single Workspace (Phase 1 Workspace de-emphasis): every member is in
  // the one Workspace, so the column says nothing. Useful only on the
  // tenant-wide view of a multi-Workspace company.
  const showWorkspacesCol = !filterWorkspaceId && hasMultipleWorkspaces

  // Client-side sort of the current page. Defaults to Department asc so
  // members open GROUPED by their department, with members ordered by
  // name within each department (the `department` accessor is a composite
  // key for exactly that). Members with no department sort first.
  // Grid view still uses this client-side department-grouped ordering.
  // The List view now renders via <DataTable>, which owns its own
  // multi-sort, so the header-driven sort state is no longer needed here.
  const { sortedRows: rows } = useSortable(rawRows, { field: 'department', direction: 'asc' }, {
    fullName: (r) => (r.fullName ?? '').toLowerCase(),
    emailId: (r) => (r.emailId ?? '').toLowerCase(),
    contactNumber: (r) => r.contactNumber ?? '',
    role: (r) => r.role ?? '',
    workRole: (r) => r.workRole ?? '',
    status: (r) => r.status ?? '',
    workspaces: (r) =>
      Number(r.workspaceCount ?? r.workspaces?.length ?? 0),
    // Composite "<department>|<name>" so sorting by Department groups by
    // department AND keeps members alphabetical within each group.
    department: (r) =>
      `${(r.departmentName ?? r.department ?? '').toLowerCase()}|${(
        r.fullName ?? ''
      ).toLowerCase()}`,
    lastActiveAt: (r) => (r.lastActiveAt ? new Date(r.lastActiveAt) : null),
  })

  // Tenant's Work Role catalogue — feeds the Work Role dropdown on
  // both the Add Member and Edit Member dialogs. The /work-roles
  // endpoint auto-seeds Manager / Team Leader / Employee on first
  // visit, so even tenants from before the catalogue shipped get a
  // working dropdown the first time this page renders.
  const workRolesQuery = useQuery({
    queryKey: ['work-roles', 'list'],
    queryFn: async () => {
      const res = await api.get('/work-roles')
      return workRoleList.parse(res.data)
    },
  })
  const activeWorkRoles = useMemo(
    () =>
      (workRolesQuery.data?.data ?? []).filter((r) => r.status === 'active'),
    [workRolesQuery.data],
  )

  const form = useForm({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      fullName: '',
      emailId: '',
      contactNumber: '',
      role: '',
      workRoleId: '',
      subAdminOperations: [],
      status: 'active',
    },
  })

  // Workspace × Department assignments for the new user. Each row =
  // one Workspace ticked in the dialog plus its optional Department
  // (empty string = "no Department in this Workspace yet"). Held in
  // React state rather than RHF because it's a derived list from the
  // workspaces query and validating it via zod would couple the schema
  // to async data.
  const [inviteAssignments, setInviteAssignments] = useState([])
  const [inviteAssignmentsError, setInviteAssignmentsError] = useState('')

  // Watched so the dialog can hide the Work Role and Workspaces/
  // Departments fields when the admin picks Sub Admin — Sub Admins
  // sit at the tenant level (above the workspace_members table) and
  // don't carry a Work Role. The Operations checkbox group is shown
  // in the reverse case (only when role === 'sub_admin').
  const inviteRole = form.watch('role')
  // The selected Work Role's visibility scope decides the Department field:
  // a company-wide role transcends Departments (field disabled), a
  // Department / Self role must belong to one (field required).
  const inviteWorkRoleId = form.watch('workRoleId')
  const inviteSelectedRole = activeWorkRoles.find(
    (r) => r.id === inviteWorkRoleId,
  )
  const inviteRoleIsCompanyWide = isCompanyWideRole(inviteSelectedRole)
  const inviteDepartmentRequired =
    inviteRole === 'user' &&
    Boolean(inviteSelectedRole) &&
    !inviteRoleIsCompanyWide

  // A company-wide Work Role has no Department — clear any prior pick (across
  // EVERY ticked Workspace, not just the sole one) so none is sent on submit
  // and every disabled control reads "— No Department —".
  useEffect(() => {
    if (inviteRoleIsCompanyWide) {
      setInviteAssignments((prev) =>
        prev.some((a) => a.departmentId)
          ? prev.map((a) => ({ ...a, departmentId: '' }))
          : prev,
      )
    }
  }, [inviteRoleIsCompanyWide])

  // Clear the role-conditional Workspace fields the moment the admin
  // flips to Sub Admin, so a previously-typed Work Role or ticked
  // Workspaces don't silently survive into the submit. Sub Admin no
  // longer carries any per-op grant list — the server auto-populates
  // it from the full SUB_ADMIN_OPERATIONS catalogue at save time, so
  // there's nothing to clear on the role-flip-back path either.
  useEffect(() => {
    if (inviteRole === 'sub_admin') {
      form.setValue('workRoleId', '', { shouldValidate: false })
      setInviteAssignments([])
      setInviteAssignmentsError('')
      return
    }
    // Single-Workspace tenant: there's no Workspace picker, so the one
    // Workspace is auto-assigned. Ensure an assignment row exists for it
    // (keeping any Department already chosen) once a member role is picked —
    // including after flipping back from Sub Admin.
    if (!hasMultipleWorkspaces && soleWorkspaceId && inviteRole) {
      setInviteAssignments((prev) =>
        prev.some((a) => a.workspaceId === soleWorkspaceId)
          ? prev
          : [{ workspaceId: soleWorkspaceId, departmentId: '' }],
      )
    }
  }, [inviteRole, hasMultipleWorkspaces, soleWorkspaceId, form])

  // Departments for the inline editor + the Department filter — keyed off
  // the picked Workspace, or the sole Workspace when a single-Workspace
  // company has no picker (so the filter loads instead of being stuck on
  // "Pick a Workspace first").
  const departmentsQuery = useQuery({
    queryKey: ['departments', effectiveWorkspaceId],
    queryFn: async () => {
      const res = await api.get(
        `/workspaces/${effectiveWorkspaceId}/departments`,
      )
      return departmentList.parse(res.data)
    },
    enabled: Boolean(effectiveWorkspaceId),
  })
  const activeDepartments =
    departmentsQuery.data?.data.filter((d) => d.status === 'active') ?? []

  // Departments for the Add AND Edit Member dialogs — fetched in
  // parallel for every Workspace so each row in the multi-workspace
  // picker can show its own Department list without a chained reload.
  // Each individual query reuses the same cache key as the table's
  // per-workspace dept query, so opening a dialog after visiting a
  // filter avoids a refetch.
  const inviteDeptQueries = useQueries({
    queries: workspaces.map((w) => ({
      queryKey: ['departments', w.id],
      queryFn: async () => {
        const res = await api.get(`/workspaces/${w.id}/departments`)
        return departmentList.parse(res.data)
      },
      // Enabled while EITHER dialog is open — both reuse
      // `inviteDeptsByWorkspace`. Previously gated on `inviteOpen` only,
      // which left the Edit dialog's Department dropdowns empty.
      enabled: inviteOpen || Boolean(editing),
    })),
  })

  /** Map of workspaceId → array of active Departments. Memoised so the
   *  picker doesn't recompute on every keystroke in the form fields. */
  const inviteDeptsByWorkspace = useMemo(() => {
    const out = new Map()
    workspaces.forEach((w, i) => {
      const list = inviteDeptQueries[i]?.data?.data ?? []
      out.set(
        w.id,
        list.filter((d) => d.status === 'active'),
      )
    })
    return out
  }, [workspaces, inviteDeptQueries])

  const inviteDeptsLoading = inviteDeptQueries.some((q) => q.isLoading)

  function toggleInviteWorkspace(wsId) {
    setInviteAssignments((prev) => {
      const has = prev.some((a) => a.workspaceId === wsId)
      return has
        ? prev.filter((a) => a.workspaceId !== wsId)
        : [...prev, { workspaceId: wsId, departmentId: '' }]
    })
    if (inviteAssignmentsError) setInviteAssignmentsError('')
  }

  function setInviteAssignmentDept(wsId, deptId) {
    setInviteAssignments((prev) =>
      prev.map((a) =>
        a.workspaceId === wsId ? { ...a, departmentId: deptId } : a,
      ),
    )
    // Picking a department resolves the "missing department" branch
    // of the validation error — clear it eagerly so the user gets
    // instant feedback that they've fixed the issue.
    if (deptId && inviteAssignmentsError) setInviteAssignmentsError('')
  }

  const inviteMutation = useMutation({
    mutationFn: async (values) => {
      // Step 1 — create the user account at tenant level. Strip the
      // catalogue reference when the role is Sub Admin — delegated
      // admins don't carry a Work Role. We no longer send a
      // `subAdminOperations` array; the backend auto-populates it
      // from the full SUB_ADMIN_OPERATIONS catalogue when the role
      // is `sub_admin` (binary permission model).
      const userPayload = {
        ...values,
        contactNumber: values.contactNumber?.trim() || undefined,
      }
      delete userPayload.subAdminOperations
      if (values.role === 'sub_admin') {
        delete userPayload.workRoleId
      } else if (!userPayload.workRoleId) {
        // Empty-string sentinel should not travel to the API — let the
        // server keep workRoleId as null when the admin somehow
        // bypassed the required check (e.g. via a stale form state).
        delete userPayload.workRoleId
      }
      // Send the chosen Workspace + Department assignments along with the
      // invite so the backend can name them in the new member's welcome
      // email. The membership rows themselves are still created by the
      // per-workspace calls in Step 2/3 below; Sub Admins carry none.
      if (values.role !== 'sub_admin' && inviteAssignments.length > 0) {
        userPayload.assignments = inviteAssignments.map((a) => ({
          workspaceId: a.workspaceId,
          departmentId: a.departmentId || null,
        }))
      }
      const res = await api.post('/users', userPayload)
      const created = inviteUserResponse.parse(res.data)

      // Sub Admin path stops here — by product rule they're tenant-
      // level and never become a row in `workspace_members`. The Add
      // Member dialog also hides the Workspaces & Departments picker
      // for this role, so there are no assignments to fan out.
      if (values.role === 'sub_admin') {
        return {
          ...created,
          role: 'sub_admin',
          successes: [],
          failures: [],
          total: 0,
        }
      }

      // Step 2/3 — for each ticked Workspace, add the user as a member
      // and (if the admin picked one) set their per-workspace Department.
      // Failures are collected so the caller can show a partial-success
      // summary; the user account is already created either way.
      const successes = []
      const failures = []
      for (const a of inviteAssignments) {
        try {
          await api.post(`/workspaces/${a.workspaceId}/members`, {
            teammateIds: [created.user.id],
          })
        } catch (err) {
          failures.push({ workspaceId: a.workspaceId, kind: 'membership', err })
          continue
        }
        if (a.departmentId) {
          try {
            await api.put(
              `/workspaces/${a.workspaceId}/members/${created.user.id}/department`,
              { departmentId: a.departmentId },
            )
          } catch (err) {
            failures.push({ workspaceId: a.workspaceId, kind: 'department', err })
            continue
          }
        }
        successes.push(a.workspaceId)
      }

      return {
        ...created,
        role: 'user',
        successes,
        failures,
        total: inviteAssignments.length,
      }
    },
    onSuccess: (result) => {
      const { successes, failures, total } = result
      if (result.role === 'sub_admin') {
        toast.success('Sub Admin created successfully.')
      } else if (failures.length === 0) {
        toast.success('User created successfully.')
      } else if (successes.length === 0) {
        const firstMsg = extractApiError(failures[0].err).message
        toast.warning(`User created, but the assignment could not be completed: ${firstMsg}`)
      } else {
        const firstFailMsg = extractApiError(failures[0].err).message
        toast.warning(
          `User created. ${successes.length} of ${total} assignments succeeded. ${failures.length} failed: ${firstFailMsg}`,
        )
      }
      setIssuedPassword({
        emailId: result.user.emailId,
        joinUrl: result.joinUrl,
      })
      setInviteOpen(false)
      setInviteAssignments([])
      setInviteAssignmentsError('')
      form.reset()
      qc.invalidateQueries({ queryKey: ['members'] })
      qc.invalidateQueries({ queryKey: ['workspaces'] })
    },
    onError: (err) => {
      // Plan-cap hit (seat limit reached): the global API interceptor
      // already surfaces the single "upgrade your plan" warning toast, so
      // suppress our own error toast to avoid a duplicate (error + warning
      // side by side). Same pattern as WorkspacesPage.
      if (isPlanLimitError(err)) return
      // Only fires when the /users POST itself rejects — assignment
      // failures are caught and rolled into onSuccess as warnings.
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          if (name in form.getValues()) {
            form.setError(name, { message: msg })
            mapped = true
          }
        }
      }
      if (!mapped) toast.error(message)
    },
  })

  /** Compute the workspace-picker error string for a given role.
   *  Returns '' when there's nothing to flag — including the cases
   *  where the picker isn't shown at all:
   *    - role === ''            → Role hasn't been picked yet; the
   *                               Workspaces & Departments picker is
   *                               hidden, so we don't surface an
   *                               error for a hidden field.
   *    - role === 'sub_admin'   → delegated admins don't get a picker.
   *  Otherwise we enforce the two rules (≥1 workspace, every
   *  ticked workspace has a department). Same function is called by
   *  both the happy-path submit and the Zod-failure callback. */
  function workspaceAssignmentsError(role) {
    if (role !== 'user') return ''
    if (inviteAssignments.length === 0) {
      // Single-Workspace: the sole Workspace is auto-assigned (role
      // effect), so an empty list here just means that hasn't resolved
      // yet — there's no Workspace picker to point at, so don't flag it.
      return hasMultipleWorkspaces
        ? 'Please select at least one Workspace.'
        : ''
    }
    // A Department / Self scoped Work Role must belong to a Department;
    // a company-wide one must NOT (its selects are disabled, so demanding
    // one was an unsatisfiable gate on multi-Workspace tenants). Same rule
    // object as the Edit dialog — see lib/member-assignments.js.
    const selectedRole = activeWorkRoles.find(
      (r) => r.id === form.getValues('workRoleId'),
    )
    // Work Role not resolvable yet — don't flag a field the admin can't fix.
    if (!selectedRole) return ''
    return departmentAssignmentError(inviteAssignments, {
      hasMultipleWorkspaces,
      soleWorkspaceId,
      companyWide: isCompanyWideRole(selectedRole),
    })
  }

  /** Happy path — Zod passed, run the workspace check and fire the
   *  mutation. */
  function onInviteSubmit(values) {
    const err = workspaceAssignmentsError(values.role)
    if (err) {
      setInviteAssignmentsError(err)
      return
    }
    inviteMutation.mutate(values)
  }

  /** Zod-failure path — at least one Zod field is invalid. We still
   *  want the workspace picker AND the Work Role to show their own
   *  errors inline (so the user fixes EVERYTHING in one pass instead
   *  of "fix Role, hit Submit again, see next error" loop).
   *
   *  Workspace error lives in local React state; Work Role error is
   *  set on the RHF form via `setError`. Both gates only fire when
   *  `role === 'user'` so the fields aren't flagged while still
   *  hidden behind an empty Role pick. */
  function onInviteInvalid() {
    const role = form.getValues('role')
    const wsErr = workspaceAssignmentsError(role)
    if (wsErr) setInviteAssignmentsError(wsErr)
    if (role === 'user') {
      const wr = form.getValues('workRoleId')
      if (!wr || wr === '') {
        form.setError(
          'workRoleId',
          { type: 'manual', message: 'Please select a Work Role.' },
          { shouldFocus: false },
        )
      }
    }
  }

  function openInvite() {
    form.reset({
      fullName: '',
      emailId: '',
      contactNumber: '',
      role: '',
      workRoleId: '',
      subAdminOperations: [],
      status: 'active',
    })
    // Pre-tick the Workspace currently filtered at the top of the page,
    // if any — common case is "I'm looking at Workspace X and want to
    // add a member to it". Admin can tick more before submitting.
    setInviteAssignments(
      filterWorkspaceId ? [{ workspaceId: filterWorkspaceId, departmentId: '' }] : [],
    )
    setInviteAssignmentsError('')
    setInviteOpen(true)
  }
  function closeInvite() {
    form.reset()
    setInviteAssignments([])
    setInviteAssignmentsError('')
    setInviteOpen(false)
  }

  // Dashboard shortcut: `?action=invite` opens the invite dialog on
  // mount. Same pattern as the other admin pages — `useRef` guards
  // against the React 18 StrictMode double-fire and we strip the
  // param so refresh / back-button doesn't re-open the dialog.
  // `searchParams` / `setSearchParams` are already declared above for
  // the `?view=grid` toggle; we reuse the same handle here.
  const handledActionRef = useRef(false)
  useEffect(() => {
    if (handledActionRef.current) return
    if (searchParams.get('action') !== 'invite') return
    handledActionRef.current = true
    openInvite()
    const next = new URLSearchParams(searchParams)
    next.delete('action')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams])

  // Renamed from `copyText` so it does not shadow the shared helper it now
  // delegates to (lib/clipboard.js), which adds the execCommand fallback for
  // the cases where navigator.clipboard is missing or REFUSED. Returns
  // whether the copy landed, so CredentialRow only flips its label on a
  // real success rather than on every click.
  async function copyValue(value, label) {
    if (!(await copyText(value))) {
      toast.error('Could not copy. Please copy manually.')
      return false
    }
    toast.success(`${label} copied.`)
    return true
  }

  // -------- Edit / Delete --------
  const currentUser = useAuthStore((s) => s.user)
  // `editing` is declared near the top (alongside `inviteOpen`) so the
  // department queries can react to the Edit dialog opening.
  const [confirmDelete, setConfirmDelete] = useState(null)

  const editForm = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: {
      fullName: '',
      contactNumber: '',
      role: 'user',
      workRoleId: '',
      subAdminOperations: [],
      status: 'active',
      statusReason: '',
    },
  })

  // Per-op picker state removed 2026-05-22 — the Sub Admin role is now
  // all-or-nothing, with the backend auto-populating the grant list
  // from the SUB_ADMIN_OPERATIONS catalogue at save time.

  // Edit-mode workspace + department picker. Mirrors the Add Member
  // shape (`{workspaceId, departmentId}[]`) so we can reuse the same
  // render block. `originalEditAssignments` is the snapshot taken when
  // the dialog opened, used to diff on save (added / removed / dept
  // changed) without a separate "is dirty" tracker.
  const [editAssignments, setEditAssignments] = useState([])
  const [originalEditAssignments, setOriginalEditAssignments] = useState([])
  // Department-rule message for the Edit dialog's picker (mirrors
  // `inviteAssignmentsError`). Local state rather than an RHF field because
  // the picker isn't registered with the form.
  const [editAssignmentsError, setEditAssignmentsError] = useState('')
  const editRoleWatch = editForm.watch('role')

  // The selected Work Role's visibility scope decides the Department field in
  // the Edit dialog too: a company-wide role transcends Departments, so the
  // control is locked and any Department already assigned is cleared on submit
  // (the diff then sends "— No Department —"). Mirrors the Add Member form.
  const editWorkRoleId = editForm.watch('workRoleId')
  const editSelectedRole = activeWorkRoles.find((r) => r.id === editWorkRoleId)
  const editRoleIsCompanyWide = isCompanyWideRole(editSelectedRole)
  // NOTE: the clearing is deliberately NOT done here by mutating
  // `editAssignments`. Doing that destroyed the member's real Department the
  // instant the admin previewed a company-wide Work Role — and nothing put it
  // back when they switched away again, so the save diff then read as "dept
  // removed" and wiped it server-side. The Department select already renders
  // "— No Department —" while `editRoleIsCompanyWide` (see the field below),
  // and the save derives the cleared value from the role being submitted, so
  // switching to a company-wide role and back is now a no-op.
  //
  // Clearing the VALIDATION MESSAGE is safe (it touches no picker data): a
  // company-wide role needs no Department, so a stale "Please select a
  // Department." must not linger after the admin switches to one.
  useEffect(() => {
    if (editRoleIsCompanyWide) setEditAssignmentsError('')
  }, [editRoleIsCompanyWide])

  // Pulls the target user's current workspace memberships so the edit
  // dialog can pre-tick and pre-pick the department per row. Skipped
  // for Company Admins (they don't have workspace_members rows).
  const editMembershipsQuery = useQuery({
    queryKey: ['user-memberships', editing?.id ?? null],
    queryFn: async () => {
      const res = await api.get(`/users/${editing.id}/memberships`)
      return res.data
    },
    // Memberships only exist for `user` rows — Company Admins and Sub
    // Admins both sit at the tenant level and never appear in
    // `workspace_members`.
    enabled:
      Boolean(editing) &&
      editing.role !== 'company_admin' &&
      editing.role !== 'sub_admin',
  })

  // Pre-fill the local picker state once memberships arrive. Stored in
  // both `editAssignments` (the editable copy) and `originalEditAssignments`
  // (the immutable baseline for the diff at save time).
  useEffect(() => {
    if (!editing) return
    const data = editMembershipsQuery.data?.data ?? []
    const original = data.map((m) => ({
      workspaceId: m.workspaceId,
      departmentId: m.departmentId ?? '',
    }))
    // Single-Workspace tenant: the member always belongs to the one
    // Workspace, so guarantee an editable assignment row for it even if the
    // membership lookup returned none. Seeded into the editable copy only
    // (not the baseline) so a genuinely-missing membership shows up as
    // "added" in the save diff and gets created.
    let editable = original
    if (
      !hasMultipleWorkspaces &&
      soleWorkspaceId &&
      !original.some((a) => a.workspaceId === soleWorkspaceId)
    ) {
      editable = [...original, { workspaceId: soleWorkspaceId, departmentId: '' }]
    }
    setEditAssignments(editable)
    setOriginalEditAssignments(original)
  }, [editing, editMembershipsQuery.data, hasMultipleWorkspaces, soleWorkspaceId])

  function toggleEditWorkspace(wsId) {
    setEditAssignments((prev) => {
      const has = prev.some((a) => a.workspaceId === wsId)
      return has
        ? prev.filter((a) => a.workspaceId !== wsId)
        : [...prev, { workspaceId: wsId, departmentId: '' }]
    })
    if (editAssignmentsError) setEditAssignmentsError('')
  }

  function setEditAssignmentDept(wsId, deptId) {
    setEditAssignments((prev) =>
      prev.map((a) =>
        a.workspaceId === wsId ? { ...a, departmentId: deptId } : a,
      ),
    )
    // Clear the "Please select a Department." flag as soon as they pick one.
    if (deptId) setEditAssignmentsError('')
  }

  /** Department rule for the Edit dialog — a Department / Self scoped Work
   *  Role must belong to a Department; only a company-wide one may go
   *  without. Returns '' when there is nothing to flag, including every case
   *  where the picker isn't rendered at all (Sub Admin / Company Admin), and
   *  while the Work Role catalogue is still resolving. */
  function editDepartmentError() {
    if (
      editRoleWatch === 'sub_admin' ||
      !editing ||
      editing.role === 'company_admin' ||
      editing.role === 'sub_admin'
    ) {
      return ''
    }
    const selected = activeWorkRoles.find((r) => r.id === editWorkRoleId)
    // Work Role not resolvable yet — don't flag a field the admin can't fix.
    if (!selected) return ''
    return departmentAssignmentError(editAssignments, {
      hasMultipleWorkspaces,
      soleWorkspaceId,
      companyWide: isCompanyWideRole(selected),
    })
  }

  /** Happy path — Zod passed; enforce the Department rule before saving. */
  function onEditSubmit(values) {
    const err = editDepartmentError()
    if (err) {
      setEditAssignmentsError(err)
      return
    }
    updateMutation.mutate(values)
  }

  /** Zod-failure path — still surface the Department error so the admin
   *  fixes everything in one pass. */
  function onEditInvalid() {
    setEditAssignmentsError(editDepartmentError())
  }

  function openEdit(row) {
    setEditing(row)
    setEditAssignmentsError('')
    // The dropdown only offers `user` / `sub_admin`. A CA row is
    // surfaced with `role: 'user'` as a harmless placeholder because
    // the Role field is hidden entirely when the editing row is a CA
    // (see Edit Member JSX) — the mutation also skips Role updates
    // for CA rows by short-circuiting on `editing.role === 'company_admin'`.
    const editingRole =
      row.role === 'sub_admin' ? 'sub_admin' : 'user'
    editForm.reset({
      emailId: row.emailId ?? '',
      fullName: row.fullName,
      contactNumber: row.contactNumber ?? '',
      role: editingRole,
      // Prefer the catalogue row reference; fall back to "" when the
      // user predates the catalogue and only carries a workRole slug.
      workRoleId: row.workRoleId ?? '',
      // Pre-tick the Sub Admin's existing operation slugs. Server
      // responses on Sub Admin rows include this array; for other
      // roles it falls back to [].
      subAdminOperations: Array.isArray(row.subAdminOperations)
        ? row.subAdminOperations
        : [],
      status: row.status === 'suspended' ? 'suspended' : 'active',
      statusReason: row.statusReason ?? '',
    })
    // Picker state is reset here too so a previous edit's selection
    // doesn't flash before the memberships query resolves.
    setEditAssignments([])
    setOriginalEditAssignments([])
  }
  function closeEdit() {
    setEditing(null)
    setEditAssignments([])
    setOriginalEditAssignments([])
    setEditAssignmentsError('')
    editForm.reset()
  }

  const updateMutation = useMutation({
    mutationFn: async (values) => {
      if (!editing) return { workspaceSummary: null }

      // 1. Patch the User row (fullName / contact / role / workRole /
      //    subAdminOperations / status). The backend owns role-flip
      //    side-effects:
      //      • Promotion to sub_admin wipes workspace_members rows
      //        (Sub Admins sit at the tenant level).
      //      • Demotion from sub_admin → user restores from the archive.
      //      • Company Admin rows can't be re-roled from this form —
      //        the Role field is hidden in the JSX for that case and
      //        the mutation skips the membership diff for them
      //        (transfer-of-ownership is its own flow elsewhere).
      const userPayload = {
        ...values,
        // Send the (trimmed) contact number, empty string included, so clearing
        // it on edit reaches the backend and resets it to null. `|| undefined`
        // dropped the key, which the update service reads as "leave unchanged".
        contactNumber: values.contactNumber?.trim() ?? '',
      }
      // Only carry a suspension reason when actually suspending; on
      // (re)activation the reason is irrelevant and the backend clears it.
      if (values.status === 'suspended') {
        userPayload.statusReason = values.statusReason?.trim() || undefined
      } else {
        delete userPayload.statusReason
      }
      // Offboarded is terminal — the Status field is read-only for these
      // members, so never let the inert form status (which defaults to
      // 'active') reactivate them on save. Drop status entirely; the
      // backend keeps their offboarded state.
      if (editing.status === 'offboarded') {
        delete userPayload.status
        delete userPayload.statusReason
      }
      // `subAdminOperations` is no longer sent from the form — the
      // backend auto-populates / clears the array based on the role
      // transition (binary model). Strip the field even if the
      // defaultValues left it on the values object.
      delete userPayload.subAdminOperations
      if (values.role === 'sub_admin') {
        delete userPayload.workRoleId
      } else if (!userPayload.workRoleId) {
        // Empty-string sentinel should not travel — let the server keep
        // whatever it already had if the form somehow lands here blank.
        delete userPayload.workRoleId
      }
      // Editing a Company Admin row never sends `role` — the Role
      // field is hidden in the JSX, and the placeholder `'user'` in
      // the form state would otherwise demote the tenant owner.
      if (editing.role === 'company_admin') {
        delete userPayload.role
      }
      await api.patch(`/users/${editing.id}`, userPayload)

      const roleChanging = values.role !== editing.role
      const skipMembershipDiff =
        editing.role === 'company_admin' || // never touched here
        values.role === 'sub_admin' || // promotion → backend cleans
        (roleChanging && editing.role === 'sub_admin') // demotion → backend restores
      if (skipMembershipDiff) {
        return { workspaceSummary: null }
      }

      // 2. Diff the picker state against the snapshot to produce three
      //    buckets: workspaces to remove, workspaces to add, and dept
      //    changes inside workspaces that already match. Each bucket
      //    fires its own request; one row's failure doesn't block the
      //    others (Promise.allSettled).
      const originalById = new Map(
        originalEditAssignments.map((a) => [a.workspaceId, a.departmentId || '']),
      )
      // Derived from the role being SUBMITTED (not from watched form state,
      // and not by having mutated `editAssignments` earlier) so previewing a
      // company-wide role and then switching back leaves the member's
      // existing Department untouched. See lib/member-assignments.js.
      const currentById = departmentsToSave(editAssignments, {
        companyWide: isCompanyWideRole(
          activeWorkRoles.find((r) => r.id === values.workRoleId),
        ),
      })

      const toRemove = [...originalById.keys()].filter(
        (wsId) => !currentById.has(wsId),
      )
      const toAdd = [...currentById.entries()].filter(
        ([wsId]) => !originalById.has(wsId),
      )
      const toReassign = [...currentById.entries()].filter(
        ([wsId, deptId]) =>
          originalById.has(wsId) && originalById.get(wsId) !== deptId,
      )

      if (
        toRemove.length === 0 &&
        toAdd.length === 0 &&
        toReassign.length === 0
      ) {
        return { workspaceSummary: null }
      }

      const wsNameById = new Map(workspaces.map((w) => [w.id, w.name]))
      const removed = []
      const added = []
      const reassigned = []
      const failures = []

      // Removals first so a re-add (workspace removed in original then
      // re-ticked under the same wsId — shouldn't happen via this UI
      // but guards against future shape changes) can't double-register.
      for (const wsId of toRemove) {
        try {
          await api.delete(`/workspaces/${wsId}/members`, {
            data: { userIds: [editing.id] },
          })
          removed.push(wsId)
        } catch (err) {
          failures.push({
            wsId,
            kind: 'remove',
            err,
          })
        }
      }

      for (const [wsId, deptId] of toAdd) {
        try {
          await api.post(`/workspaces/${wsId}/members`, {
            teammateIds: [editing.id],
          })
        } catch (err) {
          failures.push({ wsId, kind: 'add', err })
          continue
        }
        if (deptId) {
          try {
            await api.put(
              `/workspaces/${wsId}/members/${editing.id}/department`,
              { departmentId: deptId },
            )
          } catch (err) {
            failures.push({ wsId, kind: 'add_department', err })
          }
        }
        added.push(wsId)
      }

      for (const [wsId, deptId] of toReassign) {
        try {
          await api.put(
            `/workspaces/${wsId}/members/${editing.id}/department`,
            { departmentId: deptId || null },
          )
          reassigned.push(wsId)
        } catch (err) {
          failures.push({ wsId, kind: 'reassign', err })
        }
      }

      return {
        workspaceSummary: {
          added: added.map((id) => wsNameById.get(id) ?? 'a Workspace'),
          removed: removed.map((id) => wsNameById.get(id) ?? 'a Workspace'),
          reassigned: reassigned.map((id) => wsNameById.get(id) ?? 'a Workspace'),
          failures: failures.map((f) => ({
            ...f,
            workspaceName: wsNameById.get(f.wsId) ?? 'a Workspace',
            message: extractApiError(f.err).message,
          })),
        },
      }
    },
    onSuccess: (result) => {
      const summary = result?.workspaceSummary
      if (summary) {
        const parts = []
        if (summary.added.length > 0) parts.push(`added to ${summary.added.length}`)
        if (summary.removed.length > 0)
          parts.push(`removed from ${summary.removed.length}`)
        if (summary.reassigned.length > 0)
          parts.push(`Department changed in ${summary.reassigned.length}`)
        if (summary.failures.length > 0) {
          // Surface the first failure verbatim — the per-assignment
          // breakdown bloats the toast, and the admin can re-open the
          // dialog to retry the same change.
          toast.error(
            `${summary.failures.length} change${summary.failures.length === 1 ? '' : 's'} failed: ${summary.failures[0].message}`,
          )
        }
        if (parts.length > 0) {
          toast.success(`Member updated. ${parts.join(' · ')}.`)
        } else if (summary.failures.length === 0) {
          toast.success('Member updated successfully.')
        }
      } else {
        toast.success('Member updated successfully.')
      }
      closeEdit()
      qc.invalidateQueries({ queryKey: ['members'] })
      qc.invalidateQueries({ queryKey: ['user-memberships'] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          if (name in editForm.getValues()) {
            editForm.setError(name, { message: msg })
            mapped = true
          }
        }
      }
      if (!mapped) toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => {
      toast.success('User deleted successfully.')
      setConfirmDelete(null)
      qc.invalidateQueries({ queryKey: ['members'] })
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
    },
  })

  // Re-onboard an offboarded member — restores their active status / sign-in
  // (does NOT move their reassigned work back).
  const reonboardMutation = useMutation({
    mutationFn: (id) => api.post(`/admin/users/${id}/reonboard`),
    onSuccess: () => {
      toast.success('Member re-onboarded — they can sign in again.')
      setReonboardFor(null)
      qc.invalidateQueries({ queryKey: ['members'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Reset password — generates a new temp password and shows it to the admin.
  const [confirmReset, setConfirmReset] = useState(null)
  /** When set, opens the Exit Handover dialog for the leaving user. */
  const [handoverFor, setHandoverFor] = useState(null)
  const [reonboardFor, setReonboardFor] = useState(null)
  const resetMutation = useMutation({
    mutationFn: async (id) => {
      const res = await api.post(`/users/${id}/reset-password`)
      return res.data
    },
    onSuccess: (result) => {
      toast.success('Password reset successfully.')
      setConfirmReset(null)
      setIssuedPassword({
        emailId: result.user.emailId,
        temporaryPassword: result.temporaryPassword,
      })
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
    },
  })

  // Resend invitation — re-sends a pending member's Join-Company link email.
  // Only offered for members still in the `invited` (pending) state.
  const [confirmResend, setConfirmResend] = useState(null)
  const resendInviteMutation = useMutation({
    mutationFn: (id) => api.post(`/users/${id}/resend-invite`),
    onSuccess: (res) => {
      toast.success(res?.data?.message ?? 'Invitation re-sent.')
      setConfirmResend(null)
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
    },
  })

  function canManage(row) {
    // Can't manage super_admin, and edit-self goes through /profile.
    return row.role !== 'super_admin' && row.id !== currentUser?.id
  }

  // Reset to first page whenever the workspace or department filter
  // changes. Also clear the Department filter when Workspace changes
  // (or is cleared) — a stale dept-id from a previous Workspace must
  // not silently filter the next Workspace's members.
  useEffect(() => {
    setPage(1)
    setFilterDepartmentId('')
  }, [filterWorkspaceId])
  /** Fetch every member matching the current search + filters — for the
   *  export "All records" scope and select-all. Pages through at the
   *  500-row cap. */
  async function fetchAllMembers() {
    const acc = []
    let p = 1
    for (;;) {
      const res = await api.get('/users', {
        params: {
          search: debounced || undefined,
          page: p,
          pageSize: 500,
          workspaceId: listWorkspaceId || undefined,
          departmentId:
            listWorkspaceId && filterDepartmentId ? filterDepartmentId : undefined,
          workRoleId: filterWorkRoleId || undefined,
        },
      })
      const parsed = paginatedMembers.parse(res.data)
      acc.push(...parsed.data)
      const tp = parsed.pagination?.totalPages ?? 1
      if (p >= tp || p >= 50) break
      p += 1
    }
    return acc
  }
  async function getMemberExportRows(scope, ids) {
    const all = await fetchAllMembers()
    if (scope === 'selected' && ids) {
      const set = new Set(ids)
      return all.filter((u) => set.has(u.id))
    }
    return all
  }

  const MemberDash = () => (
    <span className="text-[color:var(--color-ink-subtle)]">—</span>
  )

  // DataTable column config for the List view. Task-stat cells + row
  // actions close over stable state setters / stable query refs, so the
  // memo only needs to rebuild when the visible column set or the task
  // stats change.
  const memberColumns = useMemo(() => {
    const cols = [
      {
        key: 'fullName',
        header: 'Full Name',
        grow: true,
        pinnable: true,
        minWidth: 180,
        sortAccessor: (r) => (r.fullName ?? '').toLowerCase(),
        searchAccessor: (r) => `${r.fullName ?? ''} ${r.emailId ?? ''}`,
        exportValue: (r) => r.fullName,
        render: (r) => (
          <span className="whitespace-nowrap text-[color:var(--color-ink-strong)] font-medium">
            {r.fullName}
          </span>
        ),
      },
      {
        key: 'emailId',
        header: 'Email ID',
        minWidth: 230,
        sortAccessor: (r) => (r.emailId ?? '').toLowerCase(),
        exportValue: (r) => r.emailId,
        render: (r) => <span className="whitespace-nowrap">{r.emailId}</span>,
      },
      {
        key: 'contactNumber',
        header: 'Contact Number',
        minWidth: 150,
        sortAccessor: (r) => r.contactNumber ?? '',
        exportValue: (r) => r.contactNumber ?? '',
        render: (r) => r.contactNumber ?? <MemberDash />,
      },
      showWorkspacesCol && {
        key: 'workspaces',
        header: 'Workspaces',
        minWidth: 200,
        sortAccessor: (r) =>
          Number(r.workspaceCount ?? r.workspaces?.length ?? 0),
        exportValue: (r) =>
          Array.isArray(r.workspaces)
            ? r.workspaces.map((w) => w.name).join(', ')
            : '',
        render: (r) => (
          <WorkspaceNamesCell
            workspaces={r.workspaces}
            workspaceCount={r.workspaceCount}
          />
        ),
      },
      {
        key: 'department',
        header: 'Department',
        minWidth: 170,
        sortAccessor: (r) =>
          `${(r.departmentName ?? r.department ?? '').toLowerCase()}|${(
            r.fullName ?? ''
          ).toLowerCase()}`,
        exportValue: (r) => r.departmentName ?? '',
        render: (r) =>
          r.departmentName ? (
            <span
              className="text-[color:var(--color-ink)]"
              title={`Departments across all Workspaces: ${r.departmentName}`}
            >
              {r.departmentName}
            </span>
          ) : r.role === 'user' && r.workspaceCount === 0 ? (
            // [QA-08] "In no workspace" used to read as the same "—" as
            // "unassigned", yet it breaks the member's whole app and a
            // department can't be set until they're in a workspace.
            <span
              className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10"
              title="Not a member of any Workspace — add them to a Workspace first; a department is set per Workspace."
            >
              Not in a workspace
            </span>
          ) : (
            <span
              className="text-[color:var(--color-ink-subtle)]"
              title="No department assigned in any Workspace. Filter by a Workspace to assign one."
            >
              —
            </span>
          ),
      },
      {
        key: 'role',
        header: 'Role',
        minWidth: 110,
        sortAccessor: (r) => r.role ?? '',
        exportValue: (r) => roleLabel(r.role),
        render: (r) => <RolePill {...roleBadge(r.role)} />,
      },
      {
        key: 'workRole',
        header: 'Work Role',
        minWidth: 130,
        sortAccessor: (r) => r.workRoleName ?? r.workRole ?? '',
        exportValue: (r) =>
          r.role === 'user'
            ? r.workRoleName ?? workRoleLabel(r.workRole ?? 'employee')
            : '',
        render: (r) =>
          r.role === 'user' ? (
            r.workRoleName ?? workRoleLabel(r.workRole ?? 'employee')
          ) : (
            <MemberDash />
          ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 100,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => statusLabel(r.status),
        render: (r) => <MemberStatusPill status={r.status} />,
      },
      {
        key: 'lastActiveAt',
        header: 'Last Active',
        minWidth: 140,
        sortAccessor: (r) => (r.lastActiveAt ? new Date(r.lastActiveAt) : null),
        exportValue: (r) => formatDateTime(r.lastActiveAt),
        render: (r) => (
          <span className="text-[color:var(--color-ink-muted)] whitespace-nowrap">
            {formatDateTime(r.lastActiveAt)}
          </span>
        ),
      },
      {
        key: 'open',
        header: 'Open',
        align: 'center',
        minWidth: 80,
        sortable: false,
        exportValue: (r) => statsByUser.get(r.id)?.open ?? '',
        render: (r) => (
          <TaskStatCell
            fullName={r.fullName}
            bucket="open"
            value={statsByUser.get(r.id)?.open ?? null}
            tasksLoading={tasksQuery.isLoading}
          />
        ),
      },
      {
        key: 'dueSoon',
        header: 'Due Soon (Next 30 days)',
        align: 'center',
        minWidth: 160,
        sortable: false,
        exportValue: (r) => statsByUser.get(r.id)?.dueSoon ?? '',
        render: (r) => (
          <TaskStatCell
            fullName={r.fullName}
            bucket="dueSoon"
            value={statsByUser.get(r.id)?.dueSoon ?? null}
            tasksLoading={tasksQuery.isLoading}
          />
        ),
      },
      {
        key: 'overdue',
        header: 'Overdue',
        align: 'center',
        minWidth: 96,
        sortable: false,
        exportValue: (r) => statsByUser.get(r.id)?.overdue ?? '',
        render: (r) => (
          <TaskStatCell
            fullName={r.fullName}
            bucket="overdue"
            value={statsByUser.get(r.id)?.overdue ?? null}
            tasksLoading={tasksQuery.isLoading}
            danger
          />
        ),
      },
      {
        key: 'done',
        header: 'Done',
        align: 'center',
        minWidth: 90,
        sortable: false,
        exportValue: (r) => statsByUser.get(r.id)?.completed ?? '',
        render: (r) => (
          <TaskStatCell
            fullName={r.fullName}
            bucket="completed"
            value={statsByUser.get(r.id)?.completed ?? null}
            tasksLoading={tasksQuery.isLoading}
            success
          />
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Actions for ${r.fullName}`}
            items={
              canManage(r)
                ? [
                    r.status === 'invited' && {
                      key: 'resend',
                      label: 'Resend Invitation',
                      icon: Send,
                      onClick: () => setConfirmResend(r),
                    },
                    r.status !== 'offboarded' && {
                      key: 'reset',
                      label: 'Reset Password',
                      icon: KeyRound,
                      onClick: () => setConfirmReset(r),
                    },
                    // Offboarded is a frozen, terminal record — no profile
                    // edits until the member is Re-onboarded (same rule as
                    // Reset Password above). Re-onboard flips status to active,
                    // which brings Edit back.
                    r.status !== 'offboarded' && {
                      key: 'edit', label: 'Edit', icon: Pencil, onClick: () => openEdit(r),
                    },
                    r.status === 'offboarded'
                      ? {
                          key: 'reonboard',
                          label: 'Re-onboard',
                          icon: UserPlus,
                          onClick: () => setReonboardFor(r),
                        }
                      : {
                          key: 'offboard',
                          label: 'Offboard with handover',
                          icon: UserMinus,
                          onClick: () => setHandoverFor(r),
                        },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: Trash2,
                      danger: true,
                      separatorBefore: true,
                      onClick: () => setConfirmDelete(r),
                    },
                  ]
                : []
            }
          />
        ),
      },
    ]
    return cols.filter(Boolean)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWorkspacesCol, statsByUser, tasksQuery.isLoading])

  return (
    // Viewport-bound flex column so the table card below can take the
    // remaining vertical space and own its own scrollbars. Without this
    // the table card grew to fit its content, the horizontal scrollbar
    // sat at the bottom of THAT (often far below the viewport on a
    // 25-row page), and users had to scroll the page down first just
    // to reach the scrollbar. The 8rem subtraction accounts for the
    // AdminLayout's 64px top utility header (date/time + bell + profile
    // — `h-[64px]`) PLUS the content padding (`py-8` = 32px top +
    // 32px bottom). Total chrome = 128px = 8rem.
    <div>
      <Breadcrumb current="Members" />

      <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
            Members
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {view === 'grid'
              ? "See who's on your team and how their tasks are tracking."
              : 'Add and manage company members.'}
          </p>
        </div>
        {/* The List/Grid view toggle used to live here next to Add
         *  Member; it was moved into the filter row on 2026-06-08 so
         *  it sits alongside the search + workspace + department
         *  controls (the other "how am I looking at this data?"
         *  knobs). Add Member stays at the page header because it's a
         *  create-action, not a view-modifier. */}
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            <Upload size={14} />
            Import
          </Button>
          <Button
            onClick={openInvite}
            // Only disabled while the Workspaces list is still loading. We
            // deliberately do NOT gate on `workspaces.length === 0`: a Sub
            // Admin is a tenant-level delegated admin that needs no
            // Workspace, so a brand-new organisation must be able to add
            // one before any Workspace exists. The regular-`user` path
            // still requires at least one Workspace — that's enforced
            // inside the dialog (min-1 assignment validation on submit) —
            // so an admin with no Workspaces can pick Sub Admin freely and
            // only hits a prompt if they choose the user role instead.
            disabled={workspacesQuery.isLoading}
            title={
              workspacesQuery.isLoading ? 'Loading Workspaces…' : undefined
            }
          >
            <Plus size={14} />
            Add Member
          </Button>
        </div>
      </div>

      <MemberImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['members'] })
          qc.invalidateQueries({ queryKey: ['workspaces'] })
        }}
      />


      {/* Filters — shared FilterRow primitive above the table card.
       *  Workspace + Department selects auto-size to their visible
       *  label, matching every other list page in the app.
       *
       *  View toggle (List ⇄ Grid) sits at the row's right edge,
       *  pushed there by `<FilterRow.Spacer />`. It lives in this row
       *  because it's another "how am I looking at this data?" knob —
       *  same conceptual band as search + workspace + department. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by Full Name or Email ID"
        />
        <FilterRow.Select
          ariaLabel="Department"
          placeholder={
            departmentsQuery.isLoading
              ? 'Loading…'
              : activeDepartments.length === 0
                ? 'No Departments yet'
                : 'All Departments'
          }
          value={filterDepartmentId}
          onChange={(v) => setFilterDepartmentId(v)}
          options={[
            {
              value: '',
              label: departmentsQuery.isLoading
                ? 'Loading…'
                : activeDepartments.length === 0
                  ? 'No Departments yet'
                  : 'All Departments',
            },
            ...activeDepartments.map((d) => ({ value: d.id, label: d.name })),
          ]}
        />
        <FilterRow.Select
          ariaLabel="Work Role"
          placeholder="All Work Roles"
          value={filterWorkRoleId}
          onChange={(v) => {
            setFilterWorkRoleId(v)
            setPage(1)
          }}
          options={[
            { value: '', label: 'All Work Roles' },
            ...activeWorkRoles.map((r) => ({ value: r.id, label: r.name })),
          ]}
        />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
        <FilterRow.Spacer />
        {/* View toggle — segmented control. List = the editable CRUD
         *  table; Grid = read-only task-signals cards (the former
         *  Team page). The URL holds the choice (`?view=grid`) so
         *  deep-links from elsewhere land on the right view. */}
        <div
          role="tablist"
          aria-label="Members view"
          className="inline-flex items-center rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-0.5"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            onClick={() => setView('list')}
            title="List view — full CRUD table"
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] font-medium transition-colors cursor-pointer ' +
              (view === 'list'
                ? 'bg-[color:var(--color-primary)] text-white shadow-sm'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
            }
          >
            <ListIcon size={14} />
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'grid'}
            onClick={() => setView('grid')}
            title="Grid view — task signals per member"
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] font-medium transition-colors cursor-pointer ' +
              (view === 'grid'
                ? 'bg-[color:var(--color-primary)] text-white shadow-sm'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
            }
          >
            <LayoutGrid size={14} />
            Grid
          </button>
        </div>
      </FilterRow>

      {/* List view — original CRUD table. Hidden when the grid toggle
       *  is active; the matching `<MembersGridCard>` block below
       *  takes over in that case.
       *
       *  Table card — `flex-1 min-h-0 overflow-auto` so the card
       *  itself is the single horizontal+vertical scroll container.
       *  This matters because background-painting in CSS only covers
       *  the scroll container's full scrollable canvas — not nested
       *  children. A two-layer approach (card overflow-hidden + inner
       *  overflow-auto) would leave right-scrolled rows floating over
       *  the page background because `bg-card` would only paint the
       *  visible box. With the card AS the scroll container, the
       *  white bg extends across the entire scrollable canvas. */}
      {view === 'list' ? (
        <DataTable
          tableId="app-members"
          columns={memberColumns}
          rows={rawRows}
          getRowId={(r) => r.id}
          loading={isLoading}
          error={isError ? extractApiError(error).message : null}
          searchable={false}
          selectable
          onSelectAllRecords={async () => (await fetchAllMembers()).map((u) => u.id)}
          exportBaseName="members"
          exportTitle="Members"
          getExportRows={getMemberExportRows}
          defaultSort={[{ key: 'department', dir: 'asc' }]}
          page={page}
          totalPages={pagination?.totalPages ?? 1}
          total={pagination?.totalCount ?? 0}
          limit={pageSize}
          onPageChange={setPage}
          onLimitChange={(n) => {
            setPageSize(n)
            setPage(1)
          }}
          emptyState={{
            icon: Users,
            title: 'No members found',
            body: 'Invite a teammate to populate this list, or adjust the search and filters above.',
          }}
        />
      ) : (
        <MembersGridCard
          rows={rows}
          isLoading={isLoading}
          isError={isError}
          tasksLoading={tasksQuery.isLoading}
          statsByUser={statsByUser}
          showWorkspaces={showWorkspacesCol}
          pagination={pagination}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      )}

      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title="Add Member"
        size="ml"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeInvite}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              form="invite-member-form"
              type="submit"
              loading={inviteMutation.isPending}
            >
              Add
            </Button>
          </>
        }
      >
        <form
          id="invite-member-form"
          onSubmit={form.handleSubmit(onInviteSubmit, onInviteInvalid)}
          noValidate
          className="space-y-4"
        >
          <TextField
            label="Full Name"
            required
            // The field is already labelled, so the placeholder shows an
            // EXAMPLE of the expected format rather than repeating the label
            // back as an instruction ("Enter the Full Name").
            placeholder="e.g. Priya Sharma"
            minLength={2}
            maxLength={100}
            error={form.formState.errors.fullName?.message}
            {...form.register('fullName')}
          />
          <TextField
            label="Email ID"
            required
            type="email"
            placeholder="e.g. priya.sharma@company.com"
            minLength={5}
            maxLength={254}
            error={form.formState.errors.emailId?.message}
            {...form.register('emailId')}
          />
          <PhoneInputField
            label="Contact Number"
            id="invite-contactNumber"
            value={form.watch('contactNumber') ?? ''}
            onChange={(v) =>
              form.setValue('contactNumber', v, { shouldValidate: true })
            }
            error={form.formState.errors.contactNumber?.message}
          />
          <div>
            <FieldLabel label="Role" required htmlFor="invite-role" />
            <select
              id="invite-role"
              className={
                'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer ' +
                (form.formState.errors.role?.message
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              {...form.register('role')}
            >
              <option value="" disabled hidden>
                Select a Role
              </option>
              <option value="user">User</option>
              <option value="sub_admin">Sub Admin</option>
            </select>
            {form.formState.errors.role?.message ? (
              <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {form.formState.errors.role.message}
              </p>
            ) : null}
          </div>
          {/* Sub Admin access summary was removed 2026-05-22 — the
           *  Sub Admin role grants every entry in the
           *  SUB_ADMIN_OPERATIONS catalogue automatically, and the
           *  user didn't want the read-only summary card cluttering
           *  the dialog. Billing / API Keys / plan management stay
           *  with the Company Admin (enforced server-side). */}
          {/* Work Role — only shown once the admin has actually picked
           *  a Role. Hidden when Role is still empty (so the form
           *  reads top-down: Role first, THEN the Role-specific
           *  fields) and also hidden for Sub Admin (delegated admins
           *  sit at the tenant level, above the Work Role catalogue).
           *  Options come from Administration → Work Role Management;
           *  whatever the admin has defined there (the three defaults
           *  Manager / Team Leader / Employee plus any custom roles)
           *  appears here. */}
          {inviteRole && inviteRole !== 'sub_admin' ? (
            <div>
              <FieldLabel label="Work Role" required htmlFor="invite-work-role" />
              <select
                id="invite-work-role"
                className={
                  'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:bg-[color:var(--color-canvas)] disabled:cursor-not-allowed ' +
                  (form.formState.errors.workRoleId?.message
                    ? 'border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)]')
                }
                disabled={workRolesQuery.isLoading}
                {...form.register('workRoleId')}
              >
                <option value="" disabled hidden>
                  {workRolesQuery.isLoading
                    ? 'Loading Work Roles…'
                    : activeWorkRoles.length === 0
                    ? 'No active Work Roles — add one in Role Management'
                    : 'Select a Work Role'}
                </option>
                {activeWorkRoles.map((wr) => (
                  <option key={wr.id} value={wr.id}>
                    {wr.name}
                  </option>
                ))}
              </select>
              {form.formState.errors.workRoleId?.message ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {form.formState.errors.workRoleId.message}
                </p>
              ) : null}
            </div>
          ) : null}
          {/* Workspaces & Departments picker. Tick one or more
           *  Workspaces; each ticked row exposes its own Department
           *  dropdown so the same submit can attach this user to
           *  several Workspaces with different Departments in one go.
           *  Hidden until the admin picks a Role (so the form reads
           *  Role-first) and also hidden for Sub Admin — delegated
           *  admins are tenant-level and never inserted into
           *  `workspace_members`.
           *
           *  Single-Workspace tenant: the picker collapses to a plain
           *  Department dropdown — the member is auto-attached to the one
           *  Workspace (see the role effect), so there's no Workspace to
           *  choose. */}
          {inviteRole && inviteRole !== 'sub_admin' ? (
          hasMultipleWorkspaces ? (
          <div>
            <FieldLabel
              label="Workspaces & Departments"
              required
              htmlFor="invite-workspaces-list"
            />
            <div
              id="invite-workspaces-list"
              role="group"
              aria-label="Workspaces and Departments"
              className={`max-h-72 overflow-auto rounded-md border bg-[color:var(--color-card)] ${
                inviteAssignmentsError
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]'
              }`}
            >
              {workspaces.length === 0 ? (
                <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                  No Workspaces available — create one first.
                </div>
              ) : inviteDeptsLoading ? (
                <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                  Loading Workspaces &amp; Departments…
                </div>
              ) : (
                workspaces.map((w, idx) => {
                  const assigned = inviteAssignments.find(
                    (a) => a.workspaceId === w.id,
                  )
                  const checked = Boolean(assigned)
                  const depts = inviteDeptsByWorkspace.get(w.id) ?? []
                  return (
                    <div
                      key={w.id}
                      className={`flex items-center gap-3 px-3 py-2 ${
                        idx > 0
                          ? 'border-t border-[color:var(--color-rule)]'
                          : ''
                      }`}
                    >
                      <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleInviteWorkspace(w.id)}
                          className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
                        />
                        <span className="text-sm text-[color:var(--color-ink)] truncate">
                          {w.name}
                        </span>
                      </label>
                      <select
                        aria-label="Department"
                        disabled={!checked || inviteRoleIsCompanyWide}
                        value={
                          inviteRoleIsCompanyWide
                            ? ''
                            : assigned?.departmentId || ''
                        }
                        onChange={(e) =>
                          setInviteAssignmentDept(w.id, e.target.value)
                        }
                        className="h-8 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[10rem]"
                      >
                        <option value="">— No Department —</option>
                        {depts.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )
                })
              )}
            </div>
            {inviteAssignmentsError ? (
              <p
                role="alert"
                className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
              >
                {inviteAssignmentsError}
              </p>
            ) : (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Tick one or more Workspaces and pick a Department for
                each. Department can be changed later from the Members
                table.
              </p>
            )}
          </div>
          ) : (
          <div>
            <FieldLabel
              label="Department"
              required={inviteDepartmentRequired}
              htmlFor="invite-department"
            />
            <select
              id="invite-department"
              aria-label="Department"
              disabled={inviteRoleIsCompanyWide}
              className={
                'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:bg-[color:var(--color-canvas)] disabled:opacity-60 disabled:cursor-not-allowed ' +
                (inviteAssignmentsError
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              value={
                inviteRoleIsCompanyWide
                  ? ''
                  : inviteAssignments.find(
                      (a) => a.workspaceId === soleWorkspaceId,
                    )?.departmentId || ''
              }
              onChange={(e) =>
                setInviteAssignmentDept(soleWorkspaceId, e.target.value)
              }
            >
              <option value="">
                {inviteDeptsLoading
                  ? 'Loading Departments…'
                  : '— No Department —'}
              </option>
              {(inviteDeptsByWorkspace.get(soleWorkspaceId) ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            {inviteAssignmentsError ? (
              <p
                role="alert"
                className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
              >
                {inviteAssignmentsError}
              </p>
            ) : (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                {inviteRoleIsCompanyWide
                  ? 'This Work Role has company-wide access, so no Department is needed.'
                  : inviteDepartmentRequired
                  ? 'Select the Department this member belongs to.'
                  : 'Choose a Department. You can change it later from the Members table.'}
              </p>
            )}
          </div>
          )
          ) : null}
          <div>
            <FieldLabel label="Status" required htmlFor="invite-status" />
            <select
              id="invite-status"
              className={
                'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer ' +
                (form.formState.errors.status?.message
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              {...form.register('status')}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            {form.formState.errors.status?.message ? (
              <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {form.formState.errors.status.message}
              </p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(issuedPassword)}
        onClose={() => setIssuedPassword(null)}
        title={issuedPassword?.joinUrl ? 'Invitation Sent' : 'Password Reset'}
        footer={
          <Button onClick={() => setIssuedPassword(null)}>Done</Button>
        }
      >
        {issuedPassword ? (
          issuedPassword.joinUrl ? (
            // Phase 2 — invite: no temporary password. An invitation email
            // with the single-use accept link has been sent; the link is
            // surfaced so the admin can share it if the email did not arrive.
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[color:var(--color-status-complete)]">
                <CheckCircle2 size={18} />
                <span className="font-medium">
                  An invitation email has been sent to the member.
                </span>
              </div>

              <CredentialRow
                label="Email ID"
                value={issuedPassword.emailId}
                onCopy={() => copyValue(issuedPassword.emailId, 'Email ID')}
              />
              <CredentialRow
                label="Invitation link"
                value={issuedPassword.joinUrl}
                onCopy={() =>
                  copyValue(issuedPassword.joinUrl, 'Invitation link')
                }
              />

              <p className="text-xs text-[color:var(--color-ink-muted)]">
                The member sets a password (or signs in with their existing
                one) to join. Share this link only if the email did not
                arrive — it can be used once
                {issuedPassword.joinLinkExpiresIn
                  ? ` and expires in ${issuedPassword.joinLinkExpiresIn}`
                  : ''}
                .
              </p>
            </div>
          ) : (
            // Admin password reset — show the new temporary password once.
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[color:var(--color-status-complete)]">
                <CheckCircle2 size={18} />
                <span className="font-medium">
                  Share these sign-in details with the member.
                </span>
              </div>

              <CredentialRow
                label="Email ID"
                value={issuedPassword.emailId}
                onCopy={() => copyValue(issuedPassword.emailId, 'Email ID')}
              />
              <CredentialRow
                label="Password"
                value={issuedPassword.temporaryPassword}
                onCopy={() =>
                  copyValue(issuedPassword.temporaryPassword, 'Password')
                }
              />

              <p className="text-xs text-[color:var(--color-ink-muted)]">
                This password will not be shown again.
              </p>
            </div>
          )
        ) : null}
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={closeEdit}
        title="Edit Member"
        size="ml"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeEdit}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              form="edit-member-form"
              type="submit"
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </>
        }
      >
        <form
          id="edit-member-form"
          onSubmit={editForm.handleSubmit(onEditSubmit, onEditInvalid)}
          noValidate
          className="space-y-4"
        >
          {/* The member's sign-in address. Editable, but the server refuses
           *  when this person also belongs to another company (their login is
           *  shared and not this tenant's to rename) and when it's your OWN
           *  row (that belongs on Profile, where the password is required).
           *  Both refusals come back as a field error on `emailId`. */}
          <TextField
            label="Email ID"
            required
            type="email"
            placeholder="e.g. priya.sharma@company.com"
            maxLength={100}
            error={editForm.formState.errors.emailId?.message}
            {...editForm.register('emailId')}
          />
          <TextField
            label="Full Name"
            required
            placeholder="e.g. Priya Sharma"
            minLength={2}
            maxLength={100}
            error={editForm.formState.errors.fullName?.message}
            {...editForm.register('fullName')}
          />
          <PhoneInputField
            label="Contact Number"
            id="edit-contactNumber"
            value={editForm.watch('contactNumber') ?? ''}
            onChange={(v) =>
              editForm.setValue('contactNumber', v, { shouldValidate: true })
            }
            error={editForm.formState.errors.contactNumber?.message}
          />
          {/* Role dropdown — hidden when editing a Company Admin row.
           *  CA ownership transfer is its own flow (not this dialog),
           *  and the dropdown only offers `user` / `sub_admin` so
           *  exposing it for a CA would let the form demote them on
           *  submit. The mutation also strips `role` from the payload
           *  for CA rows, so even a stale form state can't move them. */}
          {editing?.role !== 'company_admin' ? (
            <div>
              <FieldLabel label="Role" required htmlFor="edit-role" />
              <select
                id="edit-role"
                className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
                {...editForm.register('role')}
              >
                <option value="user">User</option>
                <option value="sub_admin">Sub Admin</option>
              </select>
              {editForm.formState.errors.role?.message ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {editForm.formState.errors.role.message}
                </p>
              ) : null}
            </div>
          ) : (
            <div>
              <FieldLabel label="Role" htmlFor="edit-role-readonly" />
              <input
                id="edit-role-readonly"
                readOnly
                value="Company Admin"
                className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Company Admin is the tenant owner — ownership transfer
                is handled outside this dialog.
              </p>
            </div>
          )}
          {/* Sub Admin access summary was removed 2026-05-22. Picking
           *  the Sub Admin role auto-grants every entry in the
           *  SUB_ADMIN_OPERATIONS catalogue server-side; the read-only
           *  summary card cluttered the dialog. Billing / API Keys /
           *  plan management stay with the Company Admin (enforced
           *  server-side). */}
          {/* Work Role — populated from the tenant's catalogue.
           *  Hidden for Company Admins (no Work Role) and Sub Admins
           *  (tenant-level, sits above the Work Role catalogue). */}
          {editRoleWatch !== 'sub_admin' && editing?.role !== 'company_admin' ? (
            <div>
              <FieldLabel label="Work Role" required htmlFor="edit-work-role" />
              <select
                id="edit-work-role"
                className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:bg-[color:var(--color-canvas)] disabled:cursor-not-allowed"
                disabled={workRolesQuery.isLoading}
                {...editForm.register('workRoleId')}
              >
                <option value="" disabled hidden>
                  {workRolesQuery.isLoading
                    ? 'Loading Work Roles…'
                    : activeWorkRoles.length === 0
                    ? 'No active Work Roles — add one in Role Management'
                    : 'Select a Work Role'}
                </option>
                {activeWorkRoles.map((wr) => (
                  <option key={wr.id} value={wr.id}>
                    {wr.name}
                  </option>
                ))}
              </select>
              {editForm.formState.errors.workRoleId?.message ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {editForm.formState.errors.workRoleId.message}
                </p>
              ) : null}
            </div>
          ) : null}
          <div>
            <FieldLabel label="Status" required htmlFor="edit-status" />
            {editing?.status === 'offboarded' ? (
              <>
                {/* Offboarding is a terminal state set via the exit-handover
                 *  flow, so the status is read-only here. The old form
                 *  defaulted offboarded members to "Active", which both
                 *  misrepresented their state and could silently reactivate
                 *  them on save (see the matching guard in updateMutation).
                 *  Other fields stay editable. */}
                <div className="w-full h-10 px-3 flex items-center rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
                  Offboarded
                </div>
                <p className="mt-1 text-[13px] text-[color:var(--color-ink-subtle)]">
                  To bring them back, use the Re-onboard action on the Members
                  list.
                </p>
              </>
            ) : (
              <select
                id="edit-status"
                className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
                {...editForm.register('status')}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            )}
          </div>

          {/* Suspension reason — shown only when suspending. Surfaced to
           *  the member in their lockout message (alongside the admin
           *  contact) so they understand WHY and WHO to reach for
           *  reactivation. Optional; left blank just omits the reason. */}
          {editForm.watch('status') === 'suspended' ? (
            <div>
              <FieldLabel
                label="Suspension Reason"
                htmlFor="edit-status-reason"
              />
              <textarea
                id="edit-status-reason"
                rows={2}
                placeholder="e.g. Policy violation — contact HR to resolve. (Shown to the member)"
                className="w-full px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] resize-y"
                {...editForm.register('statusReason')}
              />
              {/* Soft limit: accept past 500 and flag it; the DTO caps the save. */}
              {lengthError(editForm.watch('statusReason'), undefined, 500, 'Suspension Reason') ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {lengthError(editForm.watch('statusReason'), undefined, 500, 'Suspension Reason')}
                </p>
              ) : null}
              <p className="mt-1 text-[13px] text-[color:var(--color-ink-subtle)]">
                The member sees this reason and your contact email when they
                try to sign in.
              </p>
            </div>
          ) : null}

          {/* Workspaces & Departments — same multi-Workspace + per-row
           *  Department picker the Add Member dialog uses, pre-filled
           *  with the user's current memberships. Hidden for Company
           *  Admins AND Sub Admins (both tenant-level, never in
           *  `workspace_members`) and also when the role is being
           *  flipped to/from either of those tenant-level roles, since
           *  the backend owns those transitions (cleanup on promotion,
           *  archive-restore on demotion).
           *
           *  Single-Workspace tenant: collapses to a plain Department
           *  dropdown — the member always belongs to the one Workspace, so
           *  there's nothing to tick or untick. */}
          {editRoleWatch !== 'sub_admin' &&
          editing?.role !== 'company_admin' &&
          editing?.role !== 'sub_admin' ? (
            hasMultipleWorkspaces ? (
            <div>
              <FieldLabel
                label="Workspaces & Departments"
                htmlFor="edit-workspaces-list"
              />
              <div
                id="edit-workspaces-list"
                role="group"
                aria-label="Workspaces and Departments"
                className="max-h-72 overflow-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
              >
                {workspaces.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                    No Workspaces available — create one first.
                  </div>
                ) : editMembershipsQuery.isLoading || inviteDeptsLoading ? (
                  <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                    Loading Workspaces &amp; Departments…
                  </div>
                ) : (
                  workspaces.map((w, idx) => {
                    const assigned = editAssignments.find(
                      (a) => a.workspaceId === w.id,
                    )
                    const checked = Boolean(assigned)
                    const depts = inviteDeptsByWorkspace.get(w.id) ?? []
                    return (
                      <div
                        key={w.id}
                        className={`flex items-center gap-3 px-3 py-2 ${
                          idx > 0
                            ? 'border-t border-[color:var(--color-rule)]'
                            : ''
                        }`}
                      >
                        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEditWorkspace(w.id)}
                            className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
                          />
                          <span className="text-sm text-[color:var(--color-ink)] truncate">
                            {w.name}
                          </span>
                        </label>
                        <select
                          aria-label="Department"
                          disabled={!checked || editRoleIsCompanyWide}
                          value={
                            editRoleIsCompanyWide
                              ? ''
                              : assigned?.departmentId || ''
                          }
                          onChange={(e) =>
                            setEditAssignmentDept(w.id, e.target.value)
                          }
                          className="h-8 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[10rem]"
                        >
                          <option value="">— No Department —</option>
                          {depts.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )
                  })
                )}
              </div>
              {editAssignmentsError ? (
                <p
                  role="alert"
                  className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
                >
                  {editAssignmentsError}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  {editRoleIsCompanyWide
                    ? 'This Work Role has company-wide access, so no Department is needed in any Workspace.'
                    : 'Untick a Workspace to remove this user from it. Pick a Department for every Workspace you keep.'}
                </p>
              )}
            </div>
            ) : (
            <div>
              <FieldLabel
                label="Department"
                htmlFor="edit-department"
                required={!editRoleIsCompanyWide}
              />
              <select
                id="edit-department"
                aria-label="Department"
                disabled={editRoleIsCompanyWide}
                className={
                  'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:bg-[color:var(--color-canvas)] disabled:opacity-60 disabled:cursor-not-allowed ' +
                  (editAssignmentsError
                    ? 'border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)]')
                }
                value={
                  editRoleIsCompanyWide
                    ? ''
                    : editAssignments.find(
                        (a) => a.workspaceId === soleWorkspaceId,
                      )?.departmentId || ''
                }
                onChange={(e) =>
                  setEditAssignmentDept(soleWorkspaceId, e.target.value)
                }
              >
                <option value="">
                  {editMembershipsQuery.isLoading || inviteDeptsLoading
                    ? 'Loading Departments…'
                    : '— No Department —'}
                </option>
                {(inviteDeptsByWorkspace.get(soleWorkspaceId) ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              {editAssignmentsError ? (
                <p
                  role="alert"
                  className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
                >
                  {editAssignmentsError}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  {editRoleIsCompanyWide
                    ? 'This Work Role has company-wide access, so no Department is needed.'
                    : 'Select the Department this member belongs to.'}
                </p>
              )}
            </div>
            )
          ) : (
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              Company Admins are tenant-wide and don&apos;t belong to specific
              Workspaces or Departments.
            </p>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete.id)
        }}
        title="Delete Member"
        message={
          confirmDelete
            ? `Are you sure you want to delete ${confirmDelete.fullName}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmReset)}
        onCancel={() => setConfirmReset(null)}
        onConfirm={() => {
          if (confirmReset) resetMutation.mutate(confirmReset.id)
        }}
        title="Reset Password"
        message={
          confirmReset
            ? `Reset password for ${confirmReset.fullName}? A new temporary password will be generated and their existing sessions will be signed out.`
            : ''
        }
        confirmLabel="Reset Password"
        variant="primary"
        confirming={resetMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmResend)}
        onCancel={() => setConfirmResend(null)}
        onConfirm={() => {
          if (confirmResend) resendInviteMutation.mutate(confirmResend.id)
        }}
        title="Resend Invitation"
        message={
          confirmResend
            ? `Resend the invitation email to ${confirmResend.fullName} (${confirmResend.emailId})? A fresh sign-up link will be generated; any previous link stops working.`
            : ''
        }
        confirmLabel="Resend Invitation"
        variant="primary"
        confirming={resendInviteMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(reonboardFor)}
        title="Re-onboard Member"
        message={
          reonboardFor
            ? `Re-onboard ${reonboardFor.fullName}? They were offboarded${
                reonboardFor.offboardedToName
                  ? ` and their work was handed over to ${reonboardFor.offboardedToName}`
                  : ''
              }. Re-onboarding restores their sign-in — it does not move that work back.`
            : ''
        }
        confirmLabel="Re-onboard"
        variant="primary"
        confirming={reonboardMutation.isPending}
        onCancel={() => setReonboardFor(null)}
        onConfirm={() => {
          if (reonboardFor) reonboardMutation.mutate(reonboardFor.id)
        }}
      />

      <ExitHandoverDialog
        open={handoverFor !== null}
        userId={handoverFor?.id ?? null}
        userName={handoverFor?.fullName ?? null}
        onClose={() => setHandoverFor(null)}
        onCommitted={() => {
          // membersQuery is invalidated inside the dialog already.
          setHandoverFor(null)
        }}
      />
    </div>
  )
}

/** One label + value + Copy button row. `onCopy` must resolve to whether the
 *  copy actually landed, so the button reads "Copied" only on a real success
 *  — and then KEEPS reading "Copied" for the rest of the dialog. Previously
 *  the label was hardcoded to "Copy", so a successful copy gave no feedback
 *  at all on the control the user had just clicked. Matches `CopyableUrl`
 *  (TenantRowActions.jsx), with the toast as the immediate confirmation. */
function CredentialRow({
  label,
  value,
  onCopy,
}) {
  // Tracks WHICH value was copied rather than a bare boolean, so the label
  // stays on "Copied" for the rest of the dialog (product decision) without
  // still claiming "Copied" when this row is later handed a different value.
  const [copiedValue, setCopiedValue] = useState(null)
  const copied = copiedValue === value
  async function handleCopy() {
    if (!(await onCopy())) return
    setCopiedValue(value)
  }
  return (
    <div>
      <h3 className="text-sm font-semibold text-[color:var(--color-ink-muted)] mb-1.5 mt-0">
        {label}
      </h3>
      <div className="flex items-center gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2">
        <span className="flex-1 font-sans text-sm select-all break-all text-[color:var(--color-ink-strong)]">
          {value}
        </span>
        <Button size="sm" variant="secondary" onClick={handleCopy} title={`Copy ${label}`}>
          <Copy size={14} />
          <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>
    </div>
  )
}

/** Member account-status pill for the List view's Status column. Solid
 *  fills in light mode; softened to the tinted-pill recipe in dark mode
 *  so it doesn't glare. Mirrors the previous inline treatment. */
function MemberStatusPill({ status }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white border border-transparent dark:border-white/10 ' +
        (status === 'active'
          ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
          : status === 'suspended' || status === 'offboarded'
            ? 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300'
            : 'bg-amber-500 dark:bg-white/[0.06] dark:text-amber-300')
      }
    >
      {statusLabel(status)}
    </span>
  )
}

/** Workspace-names cell for the List view's Workspaces column.
 *  Renders the actual workspace names instead of the bare count
 *  number the column used to show. Each name is a small chip;
 *  the cell wraps if there are more than fit on one line. When
 *  the user is in zero workspaces the cell paints an amber
 *  warning chip so a Company Admin can spot "in no workspaces yet"
 *  members at a scroll — matching the visual treatment the old
 *  count-pill version used for the same case.
 *
 *  `workspaceCount` is a fallback for the transition period: if
 *  the backend hasn't yet been redeployed with the names hydration,
 *  `workspaces` will be `[]` while `workspaceCount` may still be
 *  truthy. We render a quiet "N workspaces" pill in that case so
 *  the column doesn't go blank mid-deploy. */
function WorkspaceNamesCell({ workspaces, workspaceCount }) {
  const list = Array.isArray(workspaces) ? workspaces : []
  if (list.length === 0) {
    // Two cases share the empty branch:
    //   1. Names array empty AND count is 0 → real "in no workspaces"
    //   2. Names array empty BUT count > 0 → backend still on the
    //      old shape (count only). Render the count quietly so we
    //      don't lose data during a rolling deploy.
    if (typeof workspaceCount === 'number' && workspaceCount > 0) {
      return (
        <span
          className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10"
          title={`This user is in ${workspaceCount} Workspace${workspaceCount === 1 ? '' : 's'}.`}
        >
          {workspaceCount} workspace{workspaceCount === 1 ? '' : 's'}
        </span>
      )
    }
    return (
      <span
        className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10"
        title="This user is not a member of any Workspace yet."
      >
        No workspaces
      </span>
    )
  }
  // Render each workspace as its own chip. flex-wrap so long names
  // or many memberships flow onto a second line within the cell
  // instead of stretching the column.
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((w) => (
        <span
          key={w.id}
          className="inline-flex max-w-[160px] px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 truncate dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10"
          title={w.name}
        >
          {w.name}
        </span>
      ))}
    </div>
  )
}

/** Single task-stat table cell used by the List view's 4 stat
 *  columns. Renders a clickable number that deep-links to Task
 *  Monitor pre-filtered to the member + bucket. Visual treatment
 *  mirrors the Grid view's stat strip exactly:
 *    - `—` when tasks are still loading OR the member has no
 *      tasks at all (`value === null`)
 *    - muted ink when the count is 0
 *    - strong ink when > 0
 *    - rose when `danger` + > 0 (used for Overdue)
 *    - success-green when `success` + > 0 (used for Done (all-time)) */
function TaskStatCell({
  fullName,
  bucket,
  value,
  tasksLoading,
  danger = false,
  success = false,
}) {
  const empty = tasksLoading || value === null
  const showNumber = !empty && value > 0
  const colourClass = empty
    ? 'text-[color:var(--color-ink-subtle)]'
    : !showNumber
      ? 'text-[color:var(--color-ink-muted)]'
      : danger
        ? 'text-[color:var(--color-priority-urgent)]'
        : success
          ? 'text-[color:var(--color-success)]'
          : 'text-[color:var(--color-ink-strong)]'
  return empty ? (
    <span className={`tabular-nums ${colourClass}`}>—</span>
  ) : (
    <Link
      to={buildTaskMonitorLink(fullName, bucket)}
      title={`See ${fullName}'s ${bucket === 'completed' ? 'completed' : bucket} tasks`}
      className={`tabular-nums font-semibold hover:underline ${colourClass}`}
    >
      {value}
    </Link>
  )
}

// ─── Grid view (formerly the Team page) ─────────────────────────────
//
// Renders the same member rows as the table, but as a 3-up card grid
// with per-card task-stat strips driven by `statsByUser`. Pagination
// matches the table's footer shape (rows-per-page + prev/next) so a
// user toggling between views doesn't see the page chrome shift.

function MembersGridCard({
  rows,
  isLoading,
  isError,
  tasksLoading,
  statsByUser,
  showWorkspaces,
  pagination,
  page,
  pageSize,
  setPage,
  setPageSize,
}) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] flex-1 min-h-0 overflow-auto flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto p-5">
        {isLoading ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] py-8 text-center">
            Loading…
          </p>
        ) : isError ? (
          <p className="text-sm text-[color:var(--color-priority-urgent)] py-8 text-center">
            Could not load members.
          </p>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] p-12 text-center">
            <p className="text-sm text-[color:var(--color-ink-muted)]">
              No members match those filters.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((m) => (
              <MemberCard
                key={m.id}
                member={m}
                stats={statsByUser.get(m.id) ?? null}
                tasksLoading={tasksLoading}
                showWorkspaces={showWorkspaces}
              />
            ))}
          </div>
        )}
      </div>
      {pagination ? (
        <Pagination
          page={page}
          totalPages={pagination.totalPages}
          total={pagination.totalCount}
          limit={pageSize}
          onPageChange={setPage}
          onLimitChange={(n) => {
            setPageSize(n)
            setPage(1)
          }}
        />
      ) : null}
    </div>
  )
}

/** Single member card — header (avatar + name + role pill + work
 *  role + email + last-active eyebrow), per-card stat strip
 *  (Open · Due Soon · Overdue · Done (all-time)), and footer meta
 *  (department · workspace count). Ported verbatim from the
 *  retired TeamPage with the helpers now coming from
 *  `lib/team-stats.js`. */
function MemberCard({ member: m, stats: s, tasksLoading, showWorkspaces }) {
  const hasStats = !tasksLoading && s !== null
  // Status replaced the role-tier pill on 2026-06-08 — the role pill
  // duplicated info the workRoleName subtitle already carried, and
  // for tenant-level roles (Owner / Sub Admin) the pill was the ONLY
  // signal of what kind of user they were because `workRoleName`
  // defaulted to "Employee" for them (the schema requires every user
  // to carry a workRole, "Employee" is the column default). Now the
  // subtitle line is role-aware (Owners read "Company Admin", Sub
  // Admins read "Sub Admin") and the top-right chip carries the
  // operational state instead.
  const isActive = m.status === 'active'
  const isSuspended = m.status === 'suspended'
  const isOffboarded = m.status === 'offboarded'
  // A member who was invited but hasn't accepted yet — pending, NOT active.
  // Without this the chip fell through to the "Active" default, so the Grid
  // card showed "Active" while the List showed "Invited" for the same member.
  const isInvited = m.status === 'invited'
  // Subtitle line under the name. Tenant-level roles get explicit
  // labels — `m.workRoleName` would read "Employee" for an Owner,
  // which is the bug the user flagged.
  let subtitle = null
  if (m.role === 'company_admin') {
    subtitle = 'Company Admin'
  } else if (m.role === 'sub_admin') {
    subtitle = 'Sub Admin'
  } else if (m.workRoleName) {
    subtitle = m.workRoleName
  }
  // Status chip chrome — single pill at the top-right that's always
  // rendered (no missing visual element ever). Active is the
  // neutral-positive default, painted in a quiet success tone so it
  // doesn't compete with the headline number; Suspended is rose
  // (alarm) and Offboarded is muted slate (terminal, not an alarm).
  const statusChip = isSuspended
    ? {
        label: 'Suspended',
        className:
          'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]',
      }
    : isOffboarded
      ? {
          label: 'Offboarded',
          className:
            'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
        }
      : isInvited
        ? {
            label: 'Invited',
            className:
              'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
          }
        : {
            label: 'Active',
            className:
              'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
          }
  return (
    <article
      className={
        'rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 transition-all hover:border-[color:var(--color-accent-border)] hover:shadow-[0_8px_24px_-10px_rgba(2,54,123,0.18)] hover:-translate-y-px ' +
        (!isActive ? 'opacity-80' : '')
      }
    >
      <div className="flex items-start gap-4">
        <Avatar
          name={m.fullName}
          avatarUrl={m.avatarUrl}
          size="lg"
          className={!isActive ? 'opacity-60' : undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            {/* Plain, non-clickable name — matches the List view (the name
             *  there is not a link either), for consistent behaviour across
             *  the two views. */}
            <h3 className="min-w-0 truncate text-[15px] font-semibold text-[color:var(--color-ink-strong)]">
              {m.fullName}
            </h3>
            <span
              className={
                'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold shrink-0 ' +
                statusChip.className
              }
            >
              {statusChip.label}
            </span>
          </div>
          {subtitle ? (
            <p className="text-[13px] text-[color:var(--color-ink)] mt-0.5 truncate">
              {subtitle}
            </p>
          ) : null}
          <p className="text-[12px] text-[color:var(--color-ink-subtle)] mt-0.5 truncate">
            {m.emailId}
          </p>
          {/* Contact Number — matches the dedicated column the List
           *  view shows. Only rendered when present; absent values
           *  silently skip the line instead of printing "—" so the
           *  card stays compact for members without phones on file. */}
          {m.contactNumber ? (
            <p className="text-[12px] text-[color:var(--color-ink-subtle)] mt-0.5 truncate">
              {m.contactNumber}
            </p>
          ) : null}
          {/* "Last active 3 hours ago" line removed 2026-06-08 per
           *  design request — the List view still surfaces the same
           *  value as a dedicated column, so the data isn't lost.
           *  Restore by re-adding the `buildLastActiveBadge` call
           *  and the `<p>` block from git history if a future
           *  iteration wants the eyebrow back. */}
        </div>
      </div>

      {/* Department (· Workspace) — shown directly under the email/contact so
       *  the member's org context reads with their identity, not stranded at
       *  the card's foot below the task stats. The Workspace name / count is
       *  only shown for multi-Workspace tenants (`showWorkspaces`); on a
       *  single-Workspace company it's pure noise ("Bipto - Workspace" on
       *  every card, "0 workspaces" for the admin), so it's suppressed —
       *  mirroring the List view's hidden Workspaces column. */}
      {(() => {
        const wsList = Array.isArray(m.workspaces) ? m.workspaces : []
        const hasWs =
          showWorkspaces &&
          (wsList.length > 0 || typeof m.workspaceCount === 'number')
        if (!m.departmentName && !hasWs) return null
        return (
          <div className="mt-3 text-[11.5px] text-[color:var(--color-ink-muted)] flex items-center gap-2 flex-wrap">
            {m.departmentName ? (
              <span className="truncate">{m.departmentName}</span>
            ) : null}
            {m.departmentName && hasWs ? (
              <span className="opacity-50">·</span>
            ) : null}
            {hasWs && wsList.length > 0 ? (
              <span
                className="truncate"
                title={wsList.map((w) => w.name).join(', ')}
              >
                {wsList.length === 1
                  ? wsList[0].name
                  : `${wsList[0].name} + ${wsList.length - 1} more`}
              </span>
            ) : hasWs ? (
              <span>
                {m.workspaceCount} workspace
                {m.workspaceCount === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
        )
      })()}

      <div className="mt-4 pt-3 border-t border-[color:var(--color-rule)]">
        {!hasStats ? (
          <p className="text-[12.5px] text-[color:var(--color-ink-subtle)] italic">
            {tasksLoading ? 'Loading tasks…' : 'No task data available'}
          </p>
        ) : !s ? (
          <p className="text-[12.5px] text-[color:var(--color-ink-subtle)] italic">
            No active tasks
          </p>
        ) : (
          <div className="grid grid-cols-4 divide-x divide-[color:var(--color-rule)]">
            <Link
              to={buildTaskMonitorLink(m.fullName, 'open')}
              title={`See ${m.fullName}'s open tasks`}
              className="group flex flex-col items-center py-1 rounded-md transition-colors hover:bg-[color:var(--color-card-hover)]"
            >
              <span className="text-[17px] font-bold text-[color:var(--color-ink-strong)] tabular-nums leading-none group-hover:text-[color:var(--color-accent-strong)] transition-colors">
                {s.open}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--color-ink-muted)] mt-1 font-semibold">
                Open
              </span>
            </Link>
            <Link
              to={buildTaskMonitorLink(m.fullName, 'dueSoon')}
              title={`See ${m.fullName}'s tasks due in the next ${DUE_SOON_DAYS} days`}
              className="group flex flex-col items-center py-1 rounded-md transition-colors hover:bg-[color:var(--color-card-hover)]"
            >
              <span
                className={
                  'text-[17px] font-bold tabular-nums leading-none group-hover:text-[color:var(--color-accent-strong)] transition-colors ' +
                  (s.dueSoon > 0
                    ? 'text-[color:var(--color-ink-strong)]'
                    : 'text-[color:var(--color-ink-muted)]')
                }
              >
                {s.dueSoon}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--color-ink-muted)] mt-1 font-semibold text-center leading-tight">
                Due Soon
                <br />
                <span className="normal-case tracking-normal text-[9px]">
                  (Next 30 days)
                </span>
              </span>
            </Link>
            <Link
              to={buildTaskMonitorLink(m.fullName, 'overdue')}
              title={`See ${m.fullName}'s overdue tasks`}
              className="group flex flex-col items-center py-1 rounded-md transition-colors hover:bg-[color:var(--color-card-hover)]"
            >
              <span
                className={
                  'text-[17px] font-bold tabular-nums leading-none group-hover:text-[color:var(--color-accent-strong)] transition-colors ' +
                  (s.overdue > 0
                    ? 'text-[color:var(--color-priority-urgent)]'
                    : 'text-[color:var(--color-ink-muted)]')
                }
              >
                {s.overdue}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--color-ink-muted)] mt-1 font-semibold">
                Overdue
              </span>
            </Link>
            <Link
              to={buildTaskMonitorLink(m.fullName, 'completed')}
              title={`See ${m.fullName}'s completed tasks`}
              className="group flex flex-col items-center py-1 rounded-md transition-colors hover:bg-[color:var(--color-card-hover)]"
            >
              <span
                className={
                  'text-[17px] font-bold tabular-nums leading-none group-hover:text-[color:var(--color-accent-strong)] transition-colors ' +
                  (s.completed > 0
                    ? 'text-[color:var(--color-success)]'
                    : 'text-[color:var(--color-ink-muted)]')
                }
              >
                {s.completed}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--color-ink-muted)] mt-1 font-semibold text-center leading-tight">
                Done
                <br />
                <span className="normal-case tracking-normal text-[9px]">
                  (This Week)
                </span>
              </span>
            </Link>
          </div>
        )}
      </div>

    </article>
  )
}
